/** A supplier id as this screen holds it — a Mongo id already serialised to a string. */
export type SupplierId = string;

/**
 * A supplier id exactly as it can arrive from the API: normally an already-serialised string, but
 * a populated / non-serialised document reaches the client as an object whose `toString()` yields
 * the same id, which is what the dropdown matches on.
 */
export interface SerialisableId {
  toString(): string;
}

/**
 * A supplier id list off the product payload. The API omits the field on products that have never
 * had a supplier, and legacy rows can carry blank entries, so both are part of the contract.
 */
export type SupplierIdPayload = Array<SupplierId | SerialisableId | null | undefined> | null | undefined;

/**
 * Normalises a raw id list from the API into plain string ids, dropping empty entries.
 * @param ids Raw value off the product payload.
 * @returns String ids, in source order.
 */
const toIdList = (ids: SupplierIdPayload): SupplierId[] => {
  if (!Array.isArray(ids)) {
    return [];
  }
  return ids.filter((id): id is SupplierId | SerialisableId => id !== null && id !== undefined && id !== '').map((id) => String(id));
};

/**
 * Narrows the suppliers a product consumes downstream to those it still holds itself.
 *
 * `orderSupplierId` (GET /development-orders/:id, built by `getSuppliersInUseForPO`) lists every
 * supplier reached through a Sample, Product Costing row or Purchase Order. Those modules can name
 * a supplier the product itself does not list — it was removed from the product, or was only ever
 * attached in that module — and treating those as locked disabled a dropdown row the product does
 * not hold and blocked a Product Type change on behalf of a supplier that is not on the product at
 * all (the "Product Type Cannot Be Changed" popup firing with an empty Supplier(s) field).
 *
 * A supplier therefore locks only when it is in BOTH lists.
 *
 * @param inUseSuppliers The `orderSupplierId` array off the product payload.
 * @param selectedSuppliers The product's own `suppliers` selection.
 * @returns The locked supplier ids as a Set of strings, for O(1) membership tests.
 */
export const lockedInUseSupplierIds = (inUseSuppliers: SupplierIdPayload, selectedSuppliers: SupplierIdPayload): Set<SupplierId> => {
  const selected = new Set(toIdList(selectedSuppliers));
  return new Set(toIdList(inUseSuppliers).filter((supplierId) => selected.has(supplierId)));
};
